import type { DimensionOption } from '@alisonai/alx-sdk';

/** Where the app serves a filter's values itself, because the dimension is not one the SDK routes. */
export type AppValues = 'featureTags' | 'durationBuckets' | 'scope';

export interface FilterDefinition {
  key: string;
  label: string;
  sdkDimension: string;
  valueOrder?: 'alphabetical' | 'recency';
  appValues?: AppValues;
  /** Values are machine keys (`app_promotion`, `NOT DELIVERING`) the SDK has no vocabulary for. */
  machineValues?: true;
}

export const FILTER_CATALOG = [
  {
    key: 'creativeType',
    label: 'Creative Type',
    sdkDimension: 'asset_type',
  },
  {
    key: 'format',
    label: 'Format',
    sdkDimension: 'aspect_ratio_bucket',
  },
  {
    key: 'adType',
    label: 'Ad Type',
    sdkDimension: 'ad_format',
  },
  { key: 'os', label: 'OS', sdkDimension: 'facet:os' },
  {
    key: 'platform',
    label: 'Platform',
    sdkDimension: 'provider_type',
  },
  {
    key: 'platformBreakdown',
    label: 'Platform Breakdown',
    sdkDimension: 'platform',
  },
  {
    key: 'country',
    label: 'Country',
    sdkDimension: 'country',
  },
  {
    key: 'placement',
    label: 'Placement',
    sdkDimension: 'placement',
  },
  {
    key: 'adStatus',
    label: 'Ad Status',
    sdkDimension: 'ad_status',
    machineValues: true,
  },
  {
    key: 'campaignStatus',
    label: 'Campaign Status',
    sdkDimension: 'campaign_status',
    machineValues: true,
  },
  {
    key: 'campaignObjective',
    label: 'Campaign Objective',
    sdkDimension: 'campaign_objective',
    machineValues: true,
  },
  {
    key: 'optimizationGoal',
    label: 'Optimization Goal',
    sdkDimension: 'optimization_target',
    machineValues: true,
  },
  {
    key: 'concept',
    label: 'Concept',
    sdkDimension: 'feature:Concept',
    appValues: 'featureTags',
  },
  {
    key: 'duration',
    label: 'Duration',
    sdkDimension: 'duration_bucket',
    appValues: 'durationBuckets',
  },
  {
    key: 'feature',
    label: 'Element',
    sdkDimension: 'feature',
  },
  {
    key: 'tag',
    label: 'Tag',
    sdkDimension: 'tag',
  },
  {
    key: 'creativeId',
    label: 'Creative ID',
    sdkDimension: 'creative_id',
    valueOrder: 'recency',
  },
  {
    key: 'campaignName',
    label: 'Campaign Name',
    sdkDimension: 'campaign_name',
  },
  { key: 'adSet', label: 'Ad Set', sdkDimension: 'adgroup_name' },
  { key: 'adName', label: 'Ad Name', sdkDimension: 'ad_name' },
  { key: 'brand', label: 'Brand', sdkDimension: 'product', appValues: 'scope' },
  {
    key: 'account',
    label: 'Account',
    sdkDimension: 'account',
    appValues: 'scope',
  },
] as const;

export type FilterKey = (typeof FILTER_CATALOG)[number]['key'];
/** A tenant-declared dimension, keyed by its SDK token: no fixed list can name them. */
export type CustomFilterKey = `custom:${string}`;
export type AnyFilterKey = FilterKey | CustomFilterKey;
export type ValuesVia = 'filter_panel' | 'filter_names';

export interface FilterRoute {
  key: AnyFilterKey;
  label: string;
  sdkDimension: string;
  valuesVia: ValuesVia | null;
}

export const FILTER_KEYS = FILTER_CATALOG.map((filter) => filter.key);

const CUSTOM_FILTER_KEY_PATTERN = /^custom:[A-Za-z][A-Za-z0-9_]*$/;

const BY_KEY = new Map<string, FilterDefinition>(
  FILTER_CATALOG.map((filter) => [filter.key, filter]),
);
const BY_SDK_DIMENSION = new Map<string, FilterKey>(
  FILTER_CATALOG.map((filter) => [filter.sdkDimension, filter.key]),
);

// Words a machine key spells in capitals that stay capitals in its label.
const ACRONYMS = new Set([
  'AEO',
  'API',
  'CPA',
  'CPC',
  'CPI',
  'CPM',
  'CTR',
  'KPI',
  'MMP',
  'ROAS',
  'ROI',
  'UA',
  'VO',
]);

const APP_VALUES_VIA: Record<AppValues, ValuesVia | null> = {
  featureTags: 'filter_panel',
  durationBuckets: null,
  scope: null,
};

export function isCustomFilterKey(key: string): key is CustomFilterKey {
  return CUSTOM_FILTER_KEY_PATTERN.test(key);
}

export function toSdkDimension(key: string): string {
  return BY_KEY.get(key)?.sdkDimension ?? key;
}

export function nameValueOrderOf(key: AnyFilterKey): 'alphabetical' | 'recency' | null {
  return BY_KEY.get(key)?.valueOrder ?? null;
}

/** A machine key in words: `app_promotion` → `App Promotion`, `KPI_TYPE_CTR` → `KPI Type CTR`. */
export function humanize(value: string): string {
  return value
    .split(/[_\s]+/)
    .filter((word) => word !== '')
    .map((word) => {
      const upper = word.toUpperCase();
      return ACRONYMS.has(upper) ? upper : upper.charAt(0) + word.slice(1).toLowerCase();
    })
    .join(' ');
}

export function hasMachineValues(key: string): boolean {
  return BY_KEY.get(key)?.machineValues === true;
}

export function fromSdkDimension(sdkDimension: string): AnyFilterKey {
  return (BY_SDK_DIMENSION.get(sdkDimension) ?? sdkDimension) as AnyFilterKey;
}

function customLabel(key: CustomFilterKey): string {
  return key
    .slice('custom:'.length)
    .split('_')
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** The SDK dimensions on a metric row's breakdown axis: one report may carry only one of them. */
export function breakdownAxes(dimensions: ReadonlyArray<DimensionOption>): ReadonlySet<string> {
  return new Set(
    dimensions
      .filter((dimension) => dimension.is_breakdown_axis)
      .map((dimension) => dimension.name),
  );
}

/** Every filter the scope can serve, and which value call serves it — the SDK's own `values_via`
 * for its dimensions, the app's for the few it serves itself. */
export function filterRoutes(dimensions: ReadonlyArray<DimensionOption>): FilterRoute[] {
  const servedVia = new Map<string, ValuesVia | null>(
    dimensions.map((dimension) => [dimension.name, dimension.values_via ?? null]),
  );
  const catalog = FILTER_CATALOG.flatMap((filter): FilterRoute[] => {
    const definition: FilterDefinition = filter;
    const valuesVia =
      definition.appValues === undefined
        ? (servedVia.get(definition.sdkDimension) ?? null)
        : APP_VALUES_VIA[definition.appValues];
    if (definition.appValues === undefined && valuesVia === null) return [];
    return [{ key: filter.key, label: filter.label, sdkDimension: filter.sdkDimension, valuesVia }];
  });
  const custom = [...servedVia]
    .filter(([name, via]) => isCustomFilterKey(name) && via !== null)
    .map(([name, via]): FilterRoute => {
      const key = name as CustomFilterKey;
      return { key, label: customLabel(key), sdkDimension: key, valuesVia: via };
    });
  return [...catalog, ...custom];
}

export const COMPETITION_SDK_DIMENSION = {
  competitor: 'competitor_name',
  country: 'country',
  creativeType: 'asset_media_type',
  concept: 'feature:Concept',
  feature: 'feature',
  tag: 'tag',
  format: 'aspect_ratio_bucket',
  duration: 'duration_bucket',
  creativeId: 'creative_id',
} as const;

export type CompetitionFilterKey = keyof typeof COMPETITION_SDK_DIMENSION;

export const COMPETITION_FILTER_KEYS = Object.keys(
  COMPETITION_SDK_DIMENSION,
) as CompetitionFilterKey[];

export const COMPETITION_TYPEAHEAD_KEY: CompetitionFilterKey = 'creativeId';

const COMPETITION_KEY_BY_DIMENSION = new Map<string, CompetitionFilterKey>(
  COMPETITION_FILTER_KEYS.map((key) => [COMPETITION_SDK_DIMENSION[key], key]),
);

export function toCompetitionSdkDimension(key: CompetitionFilterKey): string {
  return COMPETITION_SDK_DIMENSION[key];
}

export function fromCompetitionSdkDimension(sdkDimension: string): CompetitionFilterKey {
  return COMPETITION_KEY_BY_DIMENSION.get(sdkDimension) ?? (sdkDimension as CompetitionFilterKey);
}
